'use client';

import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { ShoppingCart, Check, ChevronLeft, Smartphone } from 'lucide-react';
import { Header } from '@/components/layout/header';
import { Footer } from '@/components/layout/footer';
import { QuantitySelector } from '@/components/product/quantity-selector';
import { OrderPanel } from '@/components/order/order-panel';
import { MobileProductDetailView } from '@/components/mobile/mobile-product-detail-view';
import { IconView } from '@/components/admin/icon-picker';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useCart } from '@/hooks/useCart';
import { formatPrice } from '@/lib/utils';
import { resolvePrice } from '@/presenters/price.presenter';
import { cn } from '@/lib/utils';
import type { Device, Product } from '@/models';

export function ProductDetailView({ product, devices }: { product: Product; devices: Device[] }) {
  const price = resolvePrice(product);
  const [quantity, setQuantity] = useState(1);
  const [selectedColor, setSelectedColor] = useState(0);
  const [isOrderPanelOpen, setIsOrderPanelOpen] = useState(false);

  const { addItem } = useCart();

  const attributes = product.attributes ?? [];
  const colors = product.colors ?? [];

  const handleAddToOrder = () => {
    addItem({
      productId: product.id,
      variantId: 'default',
      productName: product.name,
      variantName: colors[selectedColor]?.nameAr ?? '',
      price,
      showPrice: product.showPrice,
      imageUrl: product.imageUrl,
      quantity,
    });
    setIsOrderPanelOpen(true);
  };

  return (
    <>
      <MobileProductDetailView product={product} />

      <div className="hidden min-h-screen flex-col justify-between bg-background text-foreground selection:bg-primary selection:text-primary-foreground md:flex">
        <Header onOpenCart={() => setIsOrderPanelOpen(true)} />

        <main className="container mx-auto flex-1 px-4 py-8">
          <nav className="mb-8 flex items-center gap-2 text-xs font-semibold text-muted-foreground" dir="rtl">
            <Link href="/products" className="transition-colors hover:text-foreground">
              جميع المنتجات
            </Link>
            <ChevronLeft className="h-3.5 w-3.5" />
            <Link href={`/products?brand=${product.brandSlug}`} className="transition-colors hover:text-foreground">
              {product.brandName}
            </Link>
            <ChevronLeft className="h-3.5 w-3.5" />
            <span className="line-clamp-1 text-foreground/80">{product.name}</span>
          </nav>

          <div className="grid grid-cols-1 items-start gap-12 lg:grid-cols-12" dir="rtl">
            <div className="lg:col-span-7">
              <div className="relative flex aspect-square items-center justify-center overflow-hidden rounded-3xl border border-border bg-muted p-8 shadow-lg">
                <div className="relative h-full max-h-[420px] w-full">
                  <Image
                    src={product.imageUrl || '/products/galaxy-s24-ultra.svg'}
                    alt={product.name}
                    fill
                    className="object-contain"
                    priority
                    unoptimized
                  />
                </div>
              </div>
            </div>

            <div className="space-y-6 text-start lg:col-span-5">
              <Badge variant="secondary" className="px-4 py-1.5 text-xs font-bold">
                {product.brandName}
              </Badge>

              <h1 className="font-display text-3xl leading-tight font-black tracking-tight sm:text-4xl">{product.name}</h1>

              {product.showPrice && (
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-black text-primary sm:text-4xl">{formatPrice(price)}</span>
                  <span className="text-base font-bold text-muted-foreground">DA</span>
                </div>
              )}

              <p className="font-text text-sm leading-relaxed text-muted-foreground">{product.descriptionAr}</p>

              {colors.length > 0 && (
                <div className="space-y-3 pt-2">
                  <p className="text-xs font-bold text-muted-foreground">اللون:</p>
                  <div className="flex flex-wrap gap-2.5">
                    {colors.map((color, index) => (
                      <Button
                        key={index}
                        type="button"
                        variant={selectedColor === index ? 'default' : 'outline'}
                        onClick={() => setSelectedColor(index)}
                        className="gap-2 rounded-xl px-3.5 text-xs font-bold"
                      >
                        <span
                          className="h-3.5 w-3.5 rounded-full border border-border shadow-sm"
                          style={{ backgroundColor: color.hex }}
                        />
                        <span>{color.nameAr}</span>
                        {selectedColor === index && <Check />}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {attributes.length > 0 && (
                <div className="divide-y divide-border overflow-hidden rounded-2xl border border-border bg-card">
                  {attributes.map((attribute, index) => (
                    <div key={index} className="flex items-center justify-between gap-3 px-4 py-3.5 text-sm">
                      <div className="flex items-center gap-2.5">
                        <IconView name={attribute.icon} className="h-5 w-5 text-muted-foreground" />
                        <span className="font-semibold text-muted-foreground">{attribute.key}</span>
                      </div>
                      <span className="text-end font-bold">{attribute.value}</span>
                    </div>
                  ))}
                </div>
              )}

              {devices.length > 0 && (
                <div className="rounded-2xl border border-border bg-card p-4">
                  <p className="mb-3 flex items-center gap-2 text-xs font-bold text-muted-foreground">
                    <Smartphone className="h-4 w-4 text-primary" />
                    <span>متوافق مع:</span>
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {devices.map((device) => (
                      <Badge key={device.id} variant="outline" className="font-bold">
                        {device.nameAr}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>

        <div className="sticky bottom-0 z-30 border-t border-border bg-background/95 shadow-2xl backdrop-blur-xl">
          <div className="container mx-auto flex flex-col items-center justify-between gap-4 px-4 py-4 sm:flex-row" dir="rtl">
            <div className="flex items-baseline gap-3 self-start text-start sm:self-auto">
              <span className="text-xs font-bold text-muted-foreground">السعر الإجمالي</span>
              {product.showPrice && (
                <div className="flex items-baseline gap-1.5">
                  <span className="text-2xl font-black text-primary sm:text-3xl">{formatPrice(price * quantity)}</span>
                  <span className="text-xs font-bold text-muted-foreground">DA</span>
                </div>
              )}
            </div>

            <div className="flex w-full items-center gap-3 sm:w-auto">
              <QuantitySelector quantity={quantity} onQuantityChange={setQuantity} className={cn('scale-105')} />
              <Button size="lg" onClick={handleAddToOrder} className="flex-1 gap-2.5 px-8 text-base font-black sm:flex-none">
                <ShoppingCart />
                <span>إضافة إلى قائمة الطلبات</span>
              </Button>
            </div>
          </div>
        </div>

        <OrderPanel isOpen={isOrderPanelOpen} onClose={() => setIsOrderPanelOpen(false)} />

        <Footer />
      </div>
    </>
  );
}
